import React, { useState, useEffect } from 'react';
import { NATIONALITIES } from '../../constants/nationalities';

interface CityResult {
  name: string;
  country: string;
}

async function searchCities(query: string): Promise<CityResult[]> {
  if (query.length < 2) return [];
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=10&featuretype=city`,
      { headers: { 'User-Agent': 'SahayyWeb/1.0.0' } },
    );
    if (!res.ok) return [];
    const data = await res.json();
    return data
      .filter((i: any) => (i.importance || 0) >= 0.4)
      .map((i: any) => ({ name: i.display_name.split(',')[0], country: i.address?.country || '' }))
      .slice(0, 8);
  } catch {
    return [];
  }
}

interface Props {
  onConfirm: (nationality: string, city: string) => void;
  onDismiss?: () => void;
  initialNationality?: string;
  initialCity?: string;
}

export default function CommunityPicker({ onConfirm, onDismiss, initialNationality, initialCity }: Props) {
  const [selectedNationality, setSelectedNationality] = useState(initialNationality || 'Indian');
  const [nationalitySearch, setNationalitySearch] = useState('');
  const [showNationalityList, setShowNationalityList] = useState(false);

  const [citySearch, setCitySearch] = useState('');
  const [selectedCity, setSelectedCity] = useState(initialCity || '');
  const [cityResults, setCityResults] = useState<CityResult[]>([]);
  const [cityLoading, setCityLoading] = useState(false);

  const filteredNationalities = NATIONALITIES.filter(n =>
    n.toLowerCase().includes(nationalitySearch.toLowerCase())
  );

  // Debounced city search
  useEffect(() => {
    if (citySearch.length < 2) { setCityResults([]); return; }
    setCityLoading(true);
    const timer = setTimeout(async () => {
      setCityResults(await searchCities(citySearch));
      setCityLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [citySearch]);

  // Auto-detect city on mount only if no initial city provided
  useEffect(() => {
    if (initialCity) return;
    navigator.geolocation?.getCurrentPosition(async (pos) => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json&addressdetails=1&zoom=10`,
          { headers: { 'User-Agent': 'SahayyWeb/1.0.0' } },
        );
        if (res.ok) {
          const data = await res.json();
          const city = data.address?.city || data.address?.town || data.address?.county;
          if (city) setSelectedCity(city);
        }
      } catch {}
    }, () => {}, { timeout: 5000 });
  }, []);

  const canConfirm = !!selectedNationality && !!selectedCity;

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={(e) => { if (e.target === e.currentTarget) onDismiss?.(); }}>
      <div style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '2rem', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
          <img src="/sahayy-icon-nobg.png" alt="Sahayy" style={{ width: 28, height: 28, objectFit: 'contain' }} />
          <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#1a1f36', flex: 1 }}>Pick your community</h2>
          {onDismiss && (
            <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8896ab', fontSize: '1.2rem', lineHeight: 1, padding: '0.25rem' }}>✕</button>
          )}
        </div>
        <p style={{ margin: '0 0 1.5rem', color: '#8896ab', fontSize: '0.88rem' }}>
          This helps us show you relevant groups, events and services.
        </p>

        {/* Affinity Group */}
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#8896ab', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Affinity Group
        </label>
        <div style={{ position: 'relative', marginBottom: '1rem' }}>
          <input
            style={{ width: '100%', padding: '0.6rem 0.85rem', border: '1.5px solid #e2e6f0', borderRadius: '10px', fontSize: '0.9rem', color: '#1a1f36', outline: 'none', boxSizing: 'border-box', backgroundColor: '#fafafa' }}
            value={showNationalityList ? nationalitySearch : selectedNationality}
            onChange={e => { setNationalitySearch(e.target.value); setShowNationalityList(true); setSelectedNationality(''); }}
            onFocus={() => setShowNationalityList(true)}
            placeholder="Search nationality..."
          />
          {showNationalityList && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1.5px solid #e2e6f0', borderRadius: '10px', marginTop: '4px', maxHeight: '180px', overflowY: 'auto', zIndex: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
              {filteredNationalities.map(n => (
                <div key={n} style={{ padding: '0.55rem 0.85rem', cursor: 'pointer', fontSize: '0.9rem', color: '#1a1f36' }}
                  onMouseDown={() => { setSelectedNationality(n); setNationalitySearch(''); setShowNationalityList(false); }}>
                  {n}
                </div>
              ))}
              {filteredNationalities.length === 0 && (
                <div style={{ padding: '0.55rem 0.85rem', color: '#8896ab', fontSize: '0.88rem' }}>No results</div>
              )}
            </div>
          )}
        </div>

        {/* City */}
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#8896ab', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          City
        </label>
        <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
          <input
            style={{ width: '100%', padding: '0.6rem 0.85rem', border: '1.5px solid #e2e6f0', borderRadius: '10px', fontSize: '0.9rem', color: '#1a1f36', outline: 'none', boxSizing: 'border-box', backgroundColor: '#fafafa' }}
            value={selectedCity || citySearch}
            onChange={e => { setCitySearch(e.target.value); setSelectedCity(''); }}
            placeholder="Search city..."
          />
          {citySearch.length >= 2 && !selectedCity && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1.5px solid #e2e6f0', borderRadius: '10px', marginTop: '4px', maxHeight: '180px', overflowY: 'auto', zIndex: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
              {cityLoading && <div style={{ padding: '0.55rem 0.85rem', color: '#8896ab', fontSize: '0.88rem' }}>Searching...</div>}
              {!cityLoading && cityResults.map((c, i) => (
                <div key={i} style={{ padding: '0.55rem 0.85rem', cursor: 'pointer', fontSize: '0.9rem', color: '#1a1f36', display: 'flex', justifyContent: 'space-between' }}
                  onMouseDown={() => { setSelectedCity(c.name); setCitySearch(''); setCityResults([]); }}>
                  <span>{c.name}</span>
                  <span style={{ color: '#8896ab', fontSize: '0.82rem' }}>{c.country}</span>
                </div>
              ))}
              {!cityLoading && cityResults.length === 0 && (
                <div style={{ padding: '0.55rem 0.85rem', color: '#8896ab', fontSize: '0.88rem' }}>No cities found</div>
              )}
            </div>
          )}
        </div>

        {/* Preview */}
        {selectedNationality && selectedCity && (
          <div style={{ backgroundColor: '#f0f4ff', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '1.5rem', fontSize: '0.88rem', color: '#4f46e5' }}>
            <strong>{selectedNationality} in {selectedCity}</strong>
            <div style={{ color: '#6b7280', marginTop: '0.25rem', fontSize: '0.82rem' }}>
              You'll also see posts from <strong>Global in {selectedCity}</strong>.
            </div>
          </div>
        )}

        <button
          onClick={() => canConfirm && onConfirm(selectedNationality, selectedCity)}
          disabled={!canConfirm}
          style={{ width: '100%', padding: '0.75rem', backgroundColor: canConfirm ? '#FF385C' : '#e2e6f0', border: 'none', borderRadius: '12px', color: canConfirm ? '#fff' : '#8896ab', fontWeight: 700, fontSize: '0.95rem', cursor: canConfirm ? 'pointer' : 'not-allowed', transition: 'background-color 0.15s' }}>
          Browse Community
        </button>
        <p style={{ margin: '1rem 0 0', fontSize: '0.75rem', color: '#8896ab', textAlign: 'center' }}>
          To manage your corporate community, please use the Sahayy app.
        </p>
      </div>
    </div>
  );
}
